import { AkpError } from '@akpl/shared';
import { useCallback } from 'react';

import { endLiveSession } from './live';
import { useStore } from './store';
import { useT } from './useT';

/** Numeric `cap` carried by `403 queue_limit_reached` (docs/03 §2). */
function capOf(details: unknown): number {
  const cap = (details as { cap?: unknown } | undefined)?.cap;
  return typeof cap === 'number' ? cap : 0;
}

function featureOf(details: unknown): string {
  const feature = (details as { feature?: unknown } | undefined)?.feature;
  return typeof feature === 'string' ? feature : '';
}

/**
 * Maps an API failure to a toast (docs/04 §3: `409 duplicate_in_queue`, `403 feature_required`
 * with upsell copy — no purchase on the TV). `session_expired` is *not* toasted here as a
 * plain error: it hands over to `endLiveSession` (WP1.6), the single owner of the
 * "session ended" transition — toast, sign-out and TV-11 (docs/04 §5). That call is
 * idempotent, so it does not matter that the app-wide API guard usually fires first.
 *
 * Returns the error code so callers can branch (e.g. keep an overlay open).
 */
export function useApiErrorToast(): (error: unknown) => string {
  const { t } = useT();
  const showToast = useStore((s) => s.showToast);

  return useCallback(
    (error: unknown) => {
      const err = AkpError.is(error) ? error : null;
      const code = err?.code ?? 'internal';
      switch (code) {
        case 'session_expired':
          endLiveSession('expired');
          break;
        case 'duplicate_in_queue':
          showToast(t('common:queue.duplicate'), 'error');
          break;
        // `409 conflict` on the queue means the row the user pressed is stale: the item was
        // already sung, so it has to be added again (docs/03 §2 `play-now`). Without this
        // branch the drawer answered a played row with the generic failure toast (QA2 TV-65).
        case 'conflict':
          showToast(t('common:queue.alreadyPlayed'), 'error');
          break;
        case 'feature_required':
        case 'subscription_required': {
          const upsell =
            featureOf(err?.details) === 'tone.change'
              ? t('common:upsell.toneRequired')
              : t('common:upsell.featureRequired');
          showToast(`${upsell} · ${t('common:upsell.tvHint')}`, 'error');
          break;
        }
        case 'queue_limit_reached':
          showToast(t('common:queue.limitReached', { count: capOf(err?.details) }), 'error');
          break;
        case 'tone_not_available':
          showToast(t('common:errors.tone_not_available'), 'error');
          break;
        case 'song_not_available':
          showToast(t('common:errors.song_not_available'), 'error');
          break;
        case 'not_a_member':
          showToast(t('common:errors.not_a_member'), 'error');
          break;
        case 'network_error':
          showToast(t('common:errors.network_error'), 'error');
          break;
        case 'timeout':
          showToast(t('common:errors.timeout'), 'error');
          break;
        case 'rate_limited':
          showToast(t('common:errors.rate_limited'), 'error');
          break;
        default:
          showToast(t('common:errors.generic'), 'error');
          break;
      }
      return code;
    },
    [t, showToast],
  );
}
